/**
 * FeatureFlagsContext — fetches feature flags from backend on auth,
 * exposes isEnabled(key, defaultValue) for gating features.
 */
import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react'
import { apiClient } from '../api/client'
import { useAuth } from '../auth/AuthProvider'

interface FeatureFlagsContextType {
  /** Check if a flag is enabled. defaultValue used before flags load or on API error. */
  isEnabled: (key: string, defaultValue?: boolean) => boolean
  isLoaded: boolean
  flags: Record<string, boolean>
}

const FeatureFlagsContext = createContext<FeatureFlagsContextType>({
  isEnabled: () => false,
  isLoaded: false,
  flags: {},
})

export function FeatureFlagsProvider({ children }: { children: ReactNode }) {
  const [flags, setFlags] = useState<Record<string, boolean>>({})
  const [isLoaded, setIsLoaded] = useState(false)
  const { isAuthenticated } = useAuth()

  useEffect(() => {
    if (!isAuthenticated) return
    apiClient.flags
      .get()
      .then((res) => setFlags(res.data || {}))
      .catch(() => {})
      .finally(() => setIsLoaded(true))
  }, [isAuthenticated])

  const isEnabled = useCallback(
    (key: string, defaultValue = false): boolean =>
      isLoaded ? (flags[key] ?? defaultValue) : defaultValue,
    [isLoaded, flags],
  )

  return (
    <FeatureFlagsContext.Provider value={{ isEnabled, isLoaded, flags }}>
      {children}
    </FeatureFlagsContext.Provider>
  )
}

export function useFeatureFlags() {
  return useContext(FeatureFlagsContext)
}
